import PrimaryModal from '../UI/Modal/PrimaryModal';
import { PrimaryButton, SecondaryButton, ErrorButton } from "../UI";
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from "../UI/Loader/Loader";
import useComponentVisible from '../UI/ComponentVisible';
import { CheckCircle, EllipsisVertical, Palette, XCircle } from 'lucide-react';
import { getFlattenExperienceJSON, isValidJsonString, validateEmail } from '../../utils/helper';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import AgGridWrapper from '../UI/AgGrid';
import EditIcon from "../../assets/icons/edit.svg";
import NewWindowIcon from '../../assets/icons/new-window.svg';
import ArrayIcon from '../../assets/icons/array.svg';
import ManageEditInputModal from '../Experience/ManageEditInputModal';

const defaultItem = {
    "exp_json_field": "title.text",
    "input_label": "Title Text",
    "input_type": "text",
    "input_default_value": "Unlock the Power of Innovation!",
    "input_helptext": "",
    "is_mandatory": true,
    "min_length": "",
    "max_length": "",
    "group_order": "",
    "group_id": "",
    "group_title": "",
    input_options: [],
    "input_platform": "",
    "max_media_size": "",
    "accepted_media_types": [],
}

const inputTypes = [
    { "id": "text", name: "Text" },
    { "id": "number", name: "Number" },
    { "id": "radio", name: "Radio" },
    { "id": "date", name: "Date" },
    { "id": "color-picker", name: "Color Picker" },
    { "id": "rich-textarea", name: "Rich Textarea" },
    { "id": "url-textarea", name: "URL Textarea" },
    { "id": "dynamic-text", name: "Dynamic Text" },
    { "id": "array", name: "Array" },
    { "id": "selector", name: "Selector" },
    { "id": "static-options", name: "Static Options" },
    { "id": "upload", name: "Upload" },
]

const platformOptions = [
    { "id": "contentful", name: "Contentful" },
]

const mediaTypeOptions = [
    { id: 'image/*',          name: 'All Images' },
    { id: 'image/jpeg',       name: 'JPEG' },
    { id: 'image/jpg',        name: 'JPG' },
    { id: 'image/png',        name: 'PNG' },
    { id: 'image/gif',        name: 'GIF' },
    { id: 'image/webp',       name: 'WebP' },
    { id: 'image/svg+xml',    name: 'SVG' },
    { id: 'video/*',          name: 'All Videos' },
    { id: 'video/mp4',        name: 'MP4' },
    { id: 'video/webm',       name: 'WebM' },
    { id: 'application/pdf', name: 'PDF' },
    { id: 'audio/*',          name: 'All Audio' },
    { id: 'audio/mpeg',       name: 'MP3' },
    { id: 'audio/wav',        name: 'WAV' },
]

const not_mandatory_fields = [
    "input_helptext",
    "is_mandatory",
    "min_length",
    "max_length",
    "group_order",
    "group_id",
    "group_title",
    "input_platform",
    "max_media_size",
    "accepted_media_types",
]

const EditConfigJson = ({ configJsonCode = "[]", jsonSchemaCode, setConfigJsonCode, closeEditConfigJsonModal }) => {
    const { content } = useContentfulContext();
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const [loading, setLoading] = useState(false);
    const [showEditor, setShowEditor] = useState(false);

    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [configValidation, setConfigValidation] = useState({ error: "", success: "" });

    const [rowData, setRowData] = useState([defaultItem]);
    const [selectedRowData, setSelectedRowData] = useState({});

    const [showMediaTypeSelector, setShowMediaTypeSelector] = useState(false);
    const [mediaTypeSelectorRowIndex, setMediaTypeSelectorRowIndex] = useState(null);

    const [selectedCellToEdit, setSelectedCellToEdit] = useState("");


    const [deleteIds, setDeleteIds] = useState([]);


    useEffect(() => {
        if (configJsonCode && !isValidJsonString(configJsonCode)) {
            toast.error(content.EDIT_CONFIG_JSON_CONTENT.invalid_config_json);
            return;
        }

        const parsedConfigJsonCode = isValidJsonString(configJsonCode) ? JSON.parse(configJsonCode) : [];

        if (parsedConfigJsonCode.length > 0) {
            const formattedRowData = parsedConfigJsonCode.map((item) => {
                return {
                    exp_json_field: item.exp_json_field,
                    input_label: item.input.input_label,
                    input_type: item.input.input_type,
                    input_default_value: item.input.input_default_value,
                    input_helptext: item.input.input_helptext,
                    dynamic_field: item.input.dynamic_field,
                    is_mandatory: item.rules.is_mandatory,
                    min_length: item.rules.min_length,
                    max_length: item.rules.max_length,
                    group_order: item?.group?.order || "",
                    group_title: item?.group?.group_title || "",
                    group_id: item?.group?.group_id || "",
                    input_options: item.input.input_options || [],
                    input_platform: item.input.platform || "",
                    max_media_size: item.input.max_media_size || "",
                    accepted_media_types: item.input.accepted_media_types || [],
                };
            });
            setRowData([defaultItem, ...formattedRowData]);
            return;
        }

        if (!isValidJsonString(jsonSchemaCode)) {
            toast.error(content.EDIT_CONFIG_JSON_CONTENT.invalid_json_schema);
            return;
        }
        const fieldValues = getFlattenExperienceJSON(JSON.parse(jsonSchemaCode));
        const updatedRowData = [defaultItem];
        Object.keys(fieldValues).forEach((key) => {
            updatedRowData.push({
                exp_json_field: key,
                input_label: "",
                input_type: "",
                input_default_value: fieldValues[key],
                input_helptext: "",
                dynamic_field: "",
                is_mandatory: false,
                min_length: "",
                max_length: "",
                group_order: "",
                group_id: "",
                group_title: "",
                input_options: [],
            });
        });
        setRowData(updatedRowData);
    }, [])

    const handleSubmit = async () => {
        try {
            const errs = checkValidation(rowData);
            if (Object.values(errs).some((errObj) => Object.keys(errObj).length > 0)) {
                return;
            }

            setLoading(true);
            const updatedRowData = rowData.slice(1).map((item, i) => {
                const obj = {
                    order: i + 1,
                    exp_json_field: item.exp_json_field,
                    id: item.exp_json_field,
                    input: {
                        input_label: item.input_label,
                        input_type: item.input_type,
                        input_helptext: item.input_helptext,
                    },
                    rules: {
                        is_mandatory: item.is_mandatory,
                        min_length: item.min_length,
                        max_length: item.max_length,
                    },
                };
                // upload type has no default value, but has platform + media constraints
                if (item.input_type !== "upload") {
                    obj.input.input_default_value = item.input_default_value;
                } else {
                    obj.input.platform = item.input_platform || "";
                    if (item.max_media_size) obj.input.max_media_size = item.max_media_size;
                    if (item.accepted_media_types?.length) obj.input.accepted_media_types = item.accepted_media_types;
                }
                if(item?.group_order && item?.group_title && item?.group_id) {
                    obj.group = {
                        order: item.group_order,
                        group_title: item.group_title,
                        group_id: item.group_id,
                    }
                }
                if (item.input_type === "dynamic-text") {
                    obj.input.dynamic_field = item.dynamic_field;
                }

                if (item.input_type === "static-options") {
                    obj.input.input_options = item.input_options // Add input_options for static-options
                }
                return obj;
            });
            const jsonString = JSON.stringify(updatedRowData, null, 2);
            setConfigJsonCode(jsonString);
            closeEditConfigJsonModal();
        } catch (error) {
            const errMessage = error.message;
            toast.error(errMessage);
            console.error('Create organization error:', error);
        } finally {
            setLoading(false)
        }
    };

    const handleModalSave = (rowIndex, colId, newVal, dynamic_field, extraData) => {
        const updatedRow = { ...rowData[rowIndex] } // Start with a copy of the current row
        updatedRow[colId] = newVal // Update the primary value

        if (extraData && extraData?.input_options) {
            updatedRow.input_options = extraData.input_options
        }
        if (dynamic_field) {
            updatedRow.dynamic_field = dynamic_field
        }

        // Create a new array for rowData to trigger state update
        const newRowData = [...rowData]
        newRowData[rowIndex] = updatedRow

        setRowData(newRowData) // Update the state

        // After updating the state, re-run validation for the entire grid
        // Pass the newly updated data to ensure validation runs on the latest state
        checkValidation(newRowData)

        setShowEditor(false)
        setSelectedCellToEdit("")
    }

    const handleHideEditModal = () => {
        setShowEditor(false);
        setSelectedCellToEdit("");
    };

    const checkValidation = (data) => {
        const newErrors = {};
        data = data.slice(1);

        let isValid = true;

        data.forEach((row, rowIndex) => {
            rowIndex = rowIndex + 1 // because we are not checking the first row validation
            if (!newErrors[rowIndex]) newErrors[rowIndex] = {};

            if (Object.keys(row).length === 0) {
                newErrors[rowIndex][""] = "";
                isValid = false;
            }

            if (!row["exp_json_field"]) {
                newErrors[rowIndex]["exp_json_field"] = content.EDIT_CONFIG_JSON_CONTENT.errors.exp_json_field;
                isValid = false;
            }

            if (!row["input_label"]) {
                newErrors[rowIndex]["input_label"] = content.EDIT_CONFIG_JSON_CONTENT.errors.input_label;
                isValid = false;
            }

            if (!row["input_type"]) {
                newErrors[rowIndex]["input_type"] = "Input Type is required";
                isValid = false;
            } else if (!inputTypes.find((x) => x.id === row["input_type"])) {
                newErrors[rowIndex]["input_type"] = content.EDIT_CONFIG_JSON_CONTENT.errors.input_type_options;
                isValid = false;
            }

            // Only validate input_default_value if is_mandatory is true and type is not upload
            if (row["input_type"] !== "upload" && row["is_mandatory"] && !row["input_default_value"]) {
                newErrors[rowIndex]["input_default_value"] = content.EDIT_CONFIG_JSON_CONTENT.errors.input_default_value;
                isValid = false;
            }

            // For upload type, platform is required
            if (row["input_type"] === "upload" && !row["input_platform"]) {
                newErrors[rowIndex]["input_platform"] = "Platform is required for Upload type";
                isValid = false;
            }

            if (row["input_type"] === "number" && isNaN(parseInt(row["input_default_value"]))) {
                newErrors[rowIndex]["input_default_value"] = content.EDIT_CONFIG_JSON_CONTENT.errors.input_default_value_number;
                isValid = false;
            }

            // Specific validation for static-options: input_options must not be empty if input_type is static-options
            if (row["input_type"] === "static-options") {
                if (!Array.isArray(row["input_options"]) || row["input_options"].length === 0) {
                    newErrors[rowIndex]["input_options"] = "Static options are required"
                    isValid = false
                }
                // Also, default value must be one of the options
                if (
                    Array.isArray(row["input_options"]) &&
                    row["input_options"].length > 0 &&
                    !row["input_options"].includes(row["input_default_value"])
                ) {
                    newErrors[rowIndex]["input_default_value"] = "Default value must be one of the provided options"
                    isValid = false
                }
            }

            if (row["is_mandatory"] === undefined || row["is_mandatory"] === null || row["is_mandatory"] === "") {
                newErrors[rowIndex]["is_mandatory"] = content.EDIT_CONFIG_JSON_CONTENT.errors.is_mandatory;
                isValid = false;
            } else if (typeof row["is_mandatory"] !== "boolean") {
                newErrors[rowIndex]["is_mandatory"] = content.EDIT_CONFIG_JSON_CONTENT.errors.is_mandatory_boolean;
                isValid = false;
            }

            if (row["min_length"] && typeof parseInt(row["min_length"]) !== "number") {
                newErrors[rowIndex]["min_length"] = content.EDIT_CONFIG_JSON_CONTENT.errors.min_length;
                isValid = false;
            }

            if (row["max_length"] && typeof parseInt(row["max_length"]) !== "number") {
                newErrors[rowIndex]["max_length"] = content.EDIT_CONFIG_JSON_CONTENT.errors.max_length;
                isValid = false;
            }

            if (parseInt(row["min_length"]) > parseInt(row["max_length"])) {
                newErrors[rowIndex]["min_length"] = content.EDIT_CONFIG_JSON_CONTENT.errors.min_length_max_length;
                isValid = false;
            }

        })

        if (isValid) {
            setConfigValidation({ error: "", success: content.EDIT_CONFIG_JSON_CONTENT.success.data_verified });
        } else {
            setConfigValidation({ error: content.EDIT_CONFIG_JSON_CONTENT.errors.invalid_data, success: "" });
        }

        setErrors(newErrors);
        return newErrors;
    }

    const checkCellValidation = (rowIndex, colId, value, headerName) => {
        let temp = JSON.parse(JSON.stringify(errors));

        // if(not_mandatory_fields.includes(colId)) {
        //     return;
        // }

        // Skip validation for non-mandatory fields OR if the row has is_mandatory set to false
        if (not_mandatory_fields.includes(colId) || !rowData[rowIndex]?.is_mandatory) {
            // Remove any existing error for this field if it's not mandatory
            if (temp[rowIndex] && temp[rowIndex][colId]) {
                delete temp[rowIndex][colId];
                setErrors(temp);
                setCommonErrorMessage(temp);
            }
            return;
        }

        if (!temp[rowIndex]) temp[rowIndex] = {};
        const updateMessage = () => setConfigValidation({ error: content.EDIT_CONFIG_JSON_CONTENT.errors.invalid_data, success: "" });

        if (value === "" || value === null || value === undefined) {
            temp[rowIndex][colId] = `${headerName} is required`;
            setErrors(temp)
            updateMessage();
            return;
        } else {
            delete temp[rowIndex][colId];
        }

        setErrors(temp);
        setCommonErrorMessage(temp)
    }

    function setCommonErrorMessage(errs) {
        // Check if any key in the error object has an error message
        const hasError = Object.values(errs ?? errors).some(
            (errorDetails) => Object.keys(errorDetails).length > 0
        );
        if (!hasError)
            setConfigValidation({ error: "", success: content.EDIT_CONFIG_JSON_CONTENT.success.data_verified });
    }

    const handleOpenThreeDotMenu = (e, params) => {
        e.preventDefault();
        e.stopPropagation();

        // Get the bounding rectangle of the clicked icon
        const buttonRect = e.currentTarget.getBoundingClientRect();

        // Get the modal container's bounding rectangle
        const modalRect = e.currentTarget.closest(".modal-container").getBoundingClientRect();

        // Calculate the position of the menu relative to the modal
        const menuPosition = {
            top: buttonRect.bottom - modalRect.top, // Relative to modal's top
            left: buttonRect.left - modalRect.left - 192, // Relative to modal's left
        };

        // Set the menu position and toggle visibility
        setMenuPosition(menuPosition);
        setSelectedRowData(params);
        setIsComponentVisible(!isComponentVisible);
    }

    const columnDefs = useMemo(() => {
        if (rowData.length > 0) {
            // Create columns for each key in the rowData
            const columns = Object.keys(rowData[0]).map((key) => {
                // These fields use custom renderers — skip the default auto-column
                if (key === "input_options") return null
                return {
                    headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ") + `${not_mandatory_fields.includes(key) ? "" : " *"}`, // Replace underscores with spaces in header name
                    field: key,
                    sortable: true,
                    filter: true,
                    flex: 1,
                    minWidth: 250,
                    resizable: true,
                    suppressSizeToFit: true,
                    suppressColumnVirtualisation: true,
                    editable: (params) => {
                        if (params.node.rowIndex === 0) return false;
                        return true;
                    },
                    cellStyle: (params) => {
                        const rowIndex = params.node.rowIndex;
                        // Apply styling if there's an error for the field in that row
                        let classes = { textAlign: "center" };
                        if (errors[rowIndex]?.[key]) {
                            classes.border = "0.8px solid red";
                            classes.backgroundColor = "#ffe6e6";
                        }
                        if (params.node.rowIndex === 0) {
                            classes.pointerEvents = "none";
                            classes.opacity = 0.6;
                            classes.backgroundColor = "#f5f5f5";
                        }
                        return classes;
                    },
                    cellRenderer: (params) => {

                        if (key == "input_type") {
                            return <div id={params.rowIndex == 1 ? "edit-cell-id" : ""} className="flex items-center justify-between w-full h-full">
                                <select
                                    id={params.rowIndex == 1 ? "edit-select-id" : ""}
                                    className="bg-transparent border-none focus:outline-none"
                                    value={params.value}
                                    onChange={(e) => {
                                        params.setValue(e.target.value);
                                        checkCellValidation(params.node.rowIndex, key, e.target.value, params.colDef.headerName);
                                    }}
                                >
                                    <option value="" disabled>Select Type</option>
                                    {inputTypes.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        }

                        if (key == "input_platform") {
                            // Only show platform selector for upload type rows
                            if (params.data.input_type !== "upload") {
                                return (
                                    <div className="flex items-center justify-center w-full h-full text-gray-300 italic text-xs select-none">
                                        N/A
                                    </div>
                                );
                            }
                            return (
                                <div className="flex items-center justify-between w-full h-full">
                                    <select
                                        className="bg-transparent border-none focus:outline-none w-full"
                                        value={params.value || ""}
                                        onChange={(e) => {
                                            params.setValue(e.target.value);
                                            checkCellValidation(params.node.rowIndex, key, e.target.value, params.colDef.headerName);
                                        }}
                                    >
                                        <option value="" disabled>Select Platform</option>
                                        {platformOptions.map((p) => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            );
                        }

                        if (key === "max_media_size") {
                            if (params.data.input_type !== "upload") {
                                return <div className="flex items-center justify-center w-full h-full text-gray-300 italic text-xs select-none">N/A</div>;
                            }
                            return (
                                <div className="flex items-center w-full h-full gap-1">
                                    <input
                                        type="text"
                                        placeholder="e.g. 5MB or 500KB"
                                        value={params.value || ""}
                                        className="bg-transparent border-none focus:outline-none w-full text-sm"
                                        onChange={(e) => {
                                            handleModalSave(params.node.rowIndex, key, e.target.value);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            );
                        }

                        if (key === "accepted_media_types") {
                            if (params.data.input_type !== "upload") {
                                return <div className="flex items-center justify-center w-full h-full text-gray-300 italic text-xs select-none">N/A</div>;
                            }
                            const selected = Array.isArray(params.value) ? params.value : [];
                            const displayText = selected.length === 0
                                ? <span className="text-gray-400 italic text-xs">None selected</span>
                                : <span className="text-xs truncate max-w-[140px]" title={selected.join(', ')}>{selected.length} type{selected.length > 1 ? 's' : ''} selected</span>;
                            return (
                                <div className="flex items-center justify-between w-full h-full gap-1">
                                    {displayText}
                                    <img
                                        src={EditIcon}
                                        alt="Edit"
                                        className="w-4 h-4 flex-shrink-0 cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMediaTypeSelectorRowIndex(params.node.rowIndex);
                                            setShowMediaTypeSelector(true);
                                        }}
                                    />
                                </div>
                            );
                        }

                        if (key == "input_default_value") {
                            const key = params.column.colId;
                            let icon = null;
                            // Use default cell renderer for boolean values
                            if (params.data.input_type === "radio") {
                                return (
                                    <div className="w-full flex h-[100%] justify-center content-center">
                                        <input
                                            type='checkbox'
                                            checked={params.value}
                                            onChange={() => {
                                                handleModalSave(params.node.rowIndex, key, !params.value)
                                            }}
                                        />
                                    </div>
                                )

                            }

                            // Determine the icon based on the type of the value using switch case
                            switch (params.data.input_type) {
                                case "color-picker":
                                    icon = (
                                        <Palette
                                            className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                            onClick={() => {
                                                setShowEditor(true);
                                                setSelectedCellToEdit({
                                                    ...params.data,
                                                    value: params.value,
                                                    rowIndex: params.node.rowIndex,
                                                    colId: key,
                                                });
                                            }}
                                        />
                                    );
                                    break;
                                case "rich-textarea":
                                    icon = (
                                        <img
                                            src={EditIcon}
                                            alt="String"
                                            className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                            onClick={() => {
                                                setShowEditor(true);
                                                setSelectedCellToEdit({
                                                    ...params.data,
                                                    value: params.value,
                                                    rowIndex: params.node.rowIndex,
                                                    colId: key,
                                                });
                                            }}
                                        />
                                    );
                                    break;
                                case "dynamic-text":
                                    icon = (
                                        <img
                                            src={EditIcon}
                                            alt="String"
                                            className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                            onClick={() => {
                                                setShowEditor(true);
                                                setSelectedCellToEdit({
                                                    ...params.data,
                                                    value: params.value,
                                                    rowIndex: params.node.rowIndex,
                                                    colId: key,
                                                });
                                            }}
                                        />
                                    );
                                    break;
                                case "url-textarea":
                                    icon = (
                                        <img
                                            src={NewWindowIcon}
                                            alt="String"
                                            className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                            onClick={() => {
                                                if (params.value) {
                                                    window.open(params.value, "_blank");
                                                }
                                            }}
                                        />
                                    );
                                    break;
                                case "array":
                                    if (typeof params?.value == "string") {
                                        handleModalSave(params.node.rowIndex, key, params.value?.split(","))
                                    }
                                    icon = (
                                        <img
                                            src={ArrayIcon}
                                            alt="String"
                                            className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                            onClick={() => {
                                                setShowEditor(true);
                                                setSelectedCellToEdit({
                                                    ...params.data,
                                                    value: typeof params?.value == "string" ? params?.value?.split(",") : params.value,
                                                    // value: params.value?.split(","),
                                                    rowIndex: params.node.rowIndex,
                                                    colId: key,
                                                });
                                            }}
                                        />
                                    )
                                    break;
                                case "static-options": // Handle static-options default value and options management
                                    icon = (
                                        <img
                                            src={EditIcon || "/placeholder.svg"}
                                            alt="Manage Static Options"
                                            className="w-4 h-4 absolute right-2 xs:right-4 sm:right-6 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                            onClick={() => {
                                                setShowEditor(true)
                                                setSelectedCellToEdit({
                                                    ...params.data,
                                                    value: params.value, // The current default value
                                                    rowIndex: params.node.rowIndex,
                                                    colId: key,
                                                    input: {
                                                        ...params.data.input, // Keep existing input properties
                                                        input_options: params.data.input_options, // Pass the options array
                                                        input_type: "static-options", // Ensure modal knows it's a static-options
                                                    },
                                                })
                                            }}
                                        />
                                    )
                                    break
                                case "upload": // upload type has no default value
                                    return (
                                        <div className="flex items-center justify-center w-full h-full text-gray-400 italic text-xs select-none">
                                            No default value
                                        </div>
                                    )
                                default:
                                    break
                            }

                            return (
                                <div id={params.rowIndex == 1 ? "edit-cell-id" : ""} className="flex items-center justify-between w-full">
                                    <p
                                        title={params.value}
                                        className={`${icon && "max-w-[calc(100%-30px)]"
                                            } truncate`}
                                    >
                                        {typeof params.value == "object" ? params?.value?.join(",") : params.value}
                                    </p>
                                    {icon}
                                </div>
                            );
                        }

                        if (key == "is_mandatory") {
                            return (
                                <div className="flex justify-center content-center w-full h-full">
                                    <input
                                        type='checkbox'
                                        checked={params.value}
                                        onChange={() => {
                                            handleModalSave(params.node.rowIndex, key, !params.value)
                                        }}
                                    />
                                </div>
                            )
                        }

                        return <div id={params.rowIndex == 1 ? "edit-cell-id" : ""} className="flex items-center justify-between w-full h-full">
                            <p title={params.value} className={`truncate`}>
                                {params?.value}
                            </p>
                        </div>

                    },
                }
            }).filter(Boolean)

            // Add the three-dot menu column
            columns.push({
                headerName: "", // Empty header for the menu column
                field: "actions",
                maxWidth: 50,
                pinned: "right", // Pin the column to the right
                sortable: false,
                filter: false,
                resizable: false,
                suppressSizeToFit: true,
                cellRenderer: (params) => {
                    return (
                        <div
                            id={params?.rowIndex == 0 ? "first-row-action-of-preview-id" : ""}
                            onClick={(e) => handleOpenThreeDotMenu(e, params)}
                            className={`flex justify-center items-center h-full w-full`}
                        >
                            <EllipsisVertical
                                size={40}
                                color="black"
                                className={`cursor-pointer`}
                            />
                        </div>
                    );
                },
            });

            // Add a "Status" column for validation icons at the start
            columns.unshift({
                headerName: "Status",
                field: "status",
                maxWidth: 90,
                sortable: false,
                pinned: "left",
                filter: false,
                resizable: true,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                headerClass: "center-header ag-align-center-header w-full",
                cellStyle: { textAlign: "center" }, // Center-align the row numbers
                cellRenderer: (params) => {
                    const rowIndex = params.node.rowIndex;
                    const hasError =
                        errors[rowIndex] &&
                        Object.keys(errors[rowIndex]).length > 0;
                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >
                            {hasError ? (
                                <XCircle color="red" size={24} />
                            ) : (
                                <CheckCircle color="green" size={24} />
                            )}
                        </div>
                    );
                },
            });

            columns.unshift({
                headerName: "#", // Header for the row number column
                field: "",
                maxWidth: 55,
                pinned: "left", // Pin the column to the left
                sortable: false,
                filter: false,
                resizable: false,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                cellStyle: { textAlign: "center" }, // Center-align the row numbers
                valueGetter: (params) => params.node.rowIndex, // Generate row numbers dynamically
            });

            columns.unshift({
                headerName: "", // Header for the row number column
                field: "checkbox",
                maxWidth: 35,
                pinned: "left", // Pin the column to the left
                checkboxSelection: (params) => params.node.rowIndex === 0 ? false : true,
                headerCheckboxSelection: true,
                suppressMovable: true,
                resizable: false,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                cellStyle: { textAlign: "center" },
            });

            return columns;
        }
        return [];
    }, [rowData, errors]);

    const handleOnRowSelected = (event) => {
        const selectedNodes = event.api.getSelectedNodes();
        const indexes = selectedNodes.map((node) => node.rowIndex);
        setDeleteIds(indexes)
    }

    const onGridReady = (params) => {
        params.api.sizeColumnsToFit();
    };

    const onCellValueChanged = (event) => {
        const { data, rowIndex } = event;
        setRowData((prevData) => {
            const updatedData = [...prevData];
            updatedData[rowIndex] = data;
            return updatedData;
        });
    };

    const onCellEditingStopped = (event) => {
        setError("");
        checkCellValidation(event.rowIndex, event.column.colId, event.value, event.colDef.headerName);
    };

    const onSortChanged = (params) => {
        const sortedData = [];
        params.api.forEachNodeAfterFilterAndSort((node) => {
            sortedData.push(node.data);
        });

        checkValidation(sortedData);
        setRowData(sortedData);
    };

    const onCellClicked = (params) => {
        const { node, colDef } = params;
        const rowIndex = node.rowIndex;
        const field = colDef.field;

        if (errors[rowIndex]?.[field]) {
            setError(`Error: ${errors[rowIndex][field]}`);
        } else {
            setError("");
        }
    };

    const handleAddDuplicateRow = () => {
        const newRow = { ...rowData[selectedRowData.rowIndex] };
        const updatedData = [...rowData];
        updatedData.splice(selectedRowData.rowIndex + 1, 0, newRow);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    };

    const handleAddRow = () => {
        const updatedData = [...rowData];
        const newItem = {
            ...Object.keys(selectedRowData.data).reduce((acc, key) => {
                acc[key] = null; // Set all fields to null
                return acc;
            }, {}),
        };
        updatedData.splice(selectedRowData.rowIndex + 1, 0, newItem);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    }

    const handleDeleteRow = () => {
        if (deleteIds.length > 0) {
            const temp = deleteIds.filter((x) => x != 0)
            const indexesSet = new Set(temp)
            const updatedData = rowData.filter((_, index) => !indexesSet.has(index))
            setRowData(updatedData)
            setIsComponentVisible(false);
            checkValidation(updatedData);
            setDeleteIds([])
            return;
        }
        if (selectedRowData.rowIndex == 0) return;
        const updatedData = [...rowData];
        updatedData.splice(selectedRowData.rowIndex, 1);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    }

    return (
        <PrimaryModal
            title={"Config JSON"}
            hideModal={closeEditConfigJsonModal}
            widthClass="max-w-7xl"
        >
            {loading && <Loader />}

            {selectedCellToEdit && (
                <ManageEditInputModal
                    element={{
                        ...selectedCellToEdit,
                        input: {
                            input_label: selectedCellToEdit.input_label,
                            input_type: selectedCellToEdit.input_type,
                            input_default_value: selectedCellToEdit.input_default_value,
                            input_helptext: selectedCellToEdit.input_helptext,
                            input_options: selectedCellToEdit.input?.input_options,
                        },
                        rules: {
                            is_mandatory: selectedCellToEdit.is_mandatory,
                            min_length: selectedCellToEdit.min_length,
                            max_length: selectedCellToEdit.max_length,
                        }
                    }}
                    showEditor={showEditor}
                    handleSave={handleModalSave}
                    handleHideModal={handleHideEditModal}
                />
            )}

            {/* Media Type Multi-Select Modal */}
            {showMediaTypeSelector && mediaTypeSelectorRowIndex !== null && (() => {
                const currentSelected = Array.isArray(rowData[mediaTypeSelectorRowIndex]?.accepted_media_types)
                    ? rowData[mediaTypeSelectorRowIndex].accepted_media_types
                    : [];
                return (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-2xl w-80 overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                                <h3 className="font-semibold text-gray-800 text-sm">Accepted Media Types</h3>
                                <button
                                    onClick={() => setShowMediaTypeSelector(false)}
                                    className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                                >✕</button>
                            </div>
                            <div className="px-4 py-2 max-h-72 overflow-y-auto space-y-1">
                                {mediaTypeOptions.map(opt => {
                                    const checked = currentSelected.includes(opt.id);
                                    return (
                                        <label
                                            key={opt.id}
                                            className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded-md px-2 py-1.5 group"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                className="w-4 h-4 rounded accent-primary"
                                                onChange={() => {
                                                    const updated = checked
                                                        ? currentSelected.filter(x => x !== opt.id)
                                                        : [...currentSelected, opt.id];
                                                    handleModalSave(mediaTypeSelectorRowIndex, 'accepted_media_types', updated);
                                                }}
                                            />
                                            <span className="text-sm text-gray-700 flex-1">{opt.name}</span>
                                            <span className="text-xs text-gray-400 font-mono">{opt.id}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center">
                                <span className="text-xs text-gray-500">{currentSelected.length} selected</span>
                                <button
                                    onClick={() => setShowMediaTypeSelector(false)}
                                    className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-2 transition-colors"
                                >Done</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <div className="p-6 space-y-4">
                <div className={"ag-theme-quartz"} style={{ height: "100%", width: "100%" }} >
                    <div
                        id="template-config-json-edit-grid"
                        className="ag-theme-quartz z-50"
                        style={{ width: "100%" }}
                    >
                        <AgGridWrapper
                            tableId="template-config-json-edit-grid"
                            rowData={rowData}
                            columnDefs={columnDefs}
                            rowSelection="multiple"
                            suppressRowClickSelection
                            onRowSelected={handleOnRowSelected}
                            onGridReady={onGridReady}
                            onSortChanged={onSortChanged}
                            onCellValueChanged={onCellValueChanged}
                            onCellClicked={onCellClicked}
                            onCellEditingStopped={onCellEditingStopped}
                            stopEditingWhenCellsLoseFocus={true}
                        />
                    </div>

                    <div
                        ref={ref}
                        className={`absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${isComponentVisible ? "" : "hidden"}`}
                        style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
                    >
                        <div class="py-1">
                            <a id="add-duplicate-of-first-row-btn-id" onClick={handleAddDuplicateRow} href="#" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" tabIndex="-1" >
                                {content.EDIT_CONFIG_JSON_CONTENT.buttons.duplicate_row}
                            </a>
                            <a onClick={handleAddRow} href="#" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" tabIndex="-1">
                                {content.EDIT_CONFIG_JSON_CONTENT.buttons.add_new_row}
                            </a>
                            {selectedRowData.rowIndex !== 0 && <a onClick={handleDeleteRow} href="#" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" tabIndex="-1">
                                {content.EDIT_CONFIG_JSON_CONTENT.buttons.delete_row}
                            </a>}
                        </div>
                    </div>

                    <div className="mt-2">
                        <p className="text-sm italic">{content.EDIT_CONFIG_JSON_CONTENT.notes[0]}</p>
                        <p className="text-sm italic">{content.EDIT_CONFIG_JSON_CONTENT.notes[1]}</p>
                        <p className="text-sm italic">{content.EDIT_CONFIG_JSON_CONTENT.notes[2]}</p>
                        <p className="text-sm italic">{content.EDIT_CONFIG_JSON_CONTENT.notes[3]}</p>
                    </div>

                    <div className="space-y-2 mt-4 h-[48px]">
                        {!error && !configValidation.error && <div className="flex items-center h-full">
                            <p className="text-sm text-[#27942B]">
                                {configValidation.success}
                            </p>
                        </div>}
                        <div className="flex flex-col">
                            <p className="text-sm text-red-500">
                                {configValidation.error}
                            </p>
                            <p className="text-sm text-red-500">{error}</p>
                        </div>
                    </div>

                    <div className="flex items-center justify-end space-x-4 sticky bottom-0 bg-white px-6 py-2 pb-4 shadow-md">
                        <div className="space-x-4">
                            {deleteIds.length > 0 && (
                                <ErrorButton handleClick={() => handleDeleteRow(deleteIds)}>
                                    {content.EDIT_CONFIG_JSON_CONTENT.buttons.delete_multiple_rows}
                                </ErrorButton>
                            )}
                            <SecondaryButton handleClick={closeEditConfigJsonModal}>
                                {content.EDIT_CONFIG_JSON_CONTENT.buttons.cancel}
                            </SecondaryButton>
                            <PrimaryButton
                                id={"submit-btn-id"}
                                disable={loading || !!configValidation.error}
                                handleClick={handleSubmit}
                            >
                                {content.EDIT_CONFIG_JSON_CONTENT.buttons.save}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>

        </PrimaryModal>
    );
};

export default EditConfigJson;
